// src/features/variant_workflow/components/ReferenceModal.tsx
import { Alert, Button, Select } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { useReferenceModalLogic } from '../clinical_observation/hooks';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux/selectors';
import { PubMedPublication, Reference } from '../redux/types';
import { referenceFormValidationSchema } from '../validation_schemas/reference_validation_schema';

type ReferenceModalType = {
  closeModal: () => void;
  type: 'add' | 'edit';
  pubmedPublication?: PubMedPublication;
  currentValues?: Reference;
  stageType?: string;
};

const ReferenceModal = ({
  closeModal,
  type,
  pubmedPublication,
  currentValues,
  stageType,
}: ReferenceModalType) => {
  const { isReadOnly, canInteract } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isLoading,
    isAddMode,
    publicationTypes,
    boolOptions,
    titleCitekey,
    titleText,
    createModalOpts,
    initialValues,
    handleEditorChange,
    editorStates,
    getIsSaveDisabled,
    handleFormSubmit,
  } = useReferenceModalLogic({
    closeModal,
    type,
    pubmedPublication,
    currentValues,
    stageType,
  });

  // Build modal title JSX here since hooks can't return JSX
  const modalOpts = createModalOpts(
    <div>
      <div>{titleCitekey} - </div>
      <div>{titleText}</div>
    </div>,
  );

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleFormSubmit}
        validationSchema={referenceFormValidationSchema}
      >
        {({
          errors,
          values,
          handleSubmit,
          handleBlur,
          isValid,
          dirty,
          setFieldValue,
        }) => {
          // Centralized disable logic via getIsSaveDisabled
          const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">Reference type:</div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.type}
                    onChange={(v) => setFieldValue('type', v)}
                    onBlur={handleBlur}
                    className="h-proband-modal__select-field"
                    options={publicationTypes}
                    placeholder="Select reference type"
                    disabled={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.type ? errors.type : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Variant included:</div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.variant_included}
                    onChange={(v) => setFieldValue('variant_included', v)}
                    onBlur={handleBlur}
                    className="h-proband-modal__select-field"
                    options={boolOptions}
                    disabled={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.variant_included
                      ? errors.variant_included
                      : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Material and method:</div>
                <div className="c-form-field__wrapper">
                  <RichTextEditor
                    className="reference-modal__editor"
                    name="comment_materialmethod"
                    editorState={editorStates.comment_materialmethod}
                    onChange={(newState) =>
                      handleEditorChange(
                        newState,
                        setFieldValue,
                        'comment_materialmethod',
                      )
                    }
                    onBlur={() => handleBlur('comment_materialmethod')}
                    placeholder=""
                    label=""
                    readOnly={!canInteract}
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Result:</div>
                <div className="c-form-field__wrapper">
                  <RichTextEditor
                    className="reference-modal__editor"
                    name="comment_result"
                    editorState={editorStates.comment_result}
                    onChange={(newState) =>
                      handleEditorChange(
                        newState,
                        setFieldValue,
                        'comment_result',
                      )
                    }
                    onBlur={() => handleBlur('comment_result')}
                    placeholder=""
                    label=""
                    readOnly={!canInteract}
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Evidence:</div>
                <div className="c-form-field__wrapper">
                  <RichTextEditor
                    name="comment_evidence"
                    className="reference-modal__editor"
                    editorState={editorStates.comment_evidence}
                    onChange={(newState) =>
                      handleEditorChange(
                        newState,
                        setFieldValue,
                        'comment_evidence',
                      )
                    }
                    onBlur={() => handleBlur('comment_evidence')}
                    placeholder=""
                    label=""
                    readOnly={!canInteract}
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Conclusion:</div>
                <div className="c-form-field__wrapper">
                  <RichTextEditor
                    name="comment_conclusion"
                    className="reference-modal__editor"
                    editorState={editorStates.comment_conclusion}
                    onChange={(newState) =>
                      handleEditorChange(
                        newState,
                        setFieldValue,
                        'comment_conclusion',
                      )
                    }
                    onBlur={() => handleBlur('comment_conclusion')}
                    placeholder=""
                    label=""
                    readOnly={!canInteract}
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">
                  Include reference in report:
                </div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.include_in_report}
                    onChange={(v) => setFieldValue('include_in_report', v)}
                    onBlur={handleBlur}
                    className="h-proband-modal__select-field"
                    options={boolOptions}
                    disabled={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.include_in_report
                      ? errors.include_in_report
                      : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={isSaveDisabled}
                >
                  {isAddMode ? 'Save reference' : 'Save changes'}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ReferenceModal;
